import { comparer, makeAutoObservable, reaction, runInAction, toJS } from 'mobx';
import moment from 'moment-timezone';

import { rejectExtraActivityAssignment } from 'common/activeElements/modals/ModalDeleteExtraActivity/rejectExtraActivityAssignment';
import { replaceSequenceInTimelineResults } from 'common/ExtraActivityTimelineManagement/patchSequence';
import { client } from 'contexts/ApolloClient/apolloClient';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { buildMockedTimelineResults } from 'mocks/timeline/timelineMockLoader';
import { addDaysInTimezone, getStartOfDayInTimezone, subtractDaysInTimezone } from 'utils/datetime';
import { getTodayStartWithTimezone } from 'utils/datetime/currentTimeCalculations';

import { ALL_GROUPS_ID } from './components/TimeLineFilter/const';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, normalizePageSize } from './const';
import {
	saveFilters,
	loadFilters,
	deserializeCitiesToIds,
	toQueryParams as serializeToQueryParams,
	fromQueryParams,
	DEFAULT_OPERATOR_TYPE,
	DEFAULT_DISPLAY_DAYS,
	DEFAULT_TIMEZONE,
} from './filterPersistence';
import { FETCH_TIMELINE } from './qql/crewMemberTimeline';
import { CREW_TIMELINE_TEXT } from './text';
import { mapCrewTimelineTable } from './utils';

const EMPTY_FILTER_VALUES = { operatorType: DEFAULT_OPERATOR_TYPE };
const CALENDAR_RANGE_MONTHS = 3;

class CrewTimelineStore {
	isTimelineLoading = false;
	isTimelineError = false;
	error = null;
	pageCount = DEFAULT_PAGE_NUMBER;
	activePage = DEFAULT_PAGE_NUMBER;
	filters = EMPTY_FILTER_VALUES;
	selectedTimezone = DEFAULT_TIMEZONE;
	displayDays = DEFAULT_DISPLAY_DAYS;
	pageSize = DEFAULT_PAGE_SIZE;
	currentStartDate = null;
	timelineData = { drivers: [], dateRange: [], timezone: moment.tz.guess() };
	rawTimelineResults = [];
	activeItem = null;
	useMockData = false;
	isDeletingExtraActivity = false;
	_fetchRequestId = 0;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
		this.currentStartDate = getTodayStartWithTimezone(this.selectedTimezone);
		this.loadFiltersFromStorage();
		this._disposeFetch = this._createFetchReaction();
	}

	get formattedDateRange() {
		return { from: new Date(this.currentStartDate) };
	}

	loadFiltersFromStorage = () => {
		const stored = loadFilters();

		if (!stored) return;

		const {
			group,
			status,
			operatorType,
			displayDays,
			pageSize,
			selectedTimezone,
			locationAvailabilityCity,
		} = stored;

		runInAction(() => {
			if (group && group !== '' && group !== ALL_GROUPS_ID) {
				this.filters.groupsIds = [group];
			}

			if (status && status !== '') {
				this.filters.status = status;
			}

			if (operatorType) {
				this.filters.operatorType = operatorType;
			}

			if (displayDays) {
				this.displayDays = Number(displayDays) || DEFAULT_DISPLAY_DAYS;
			}

			if (pageSize !== undefined) {
				this.pageSize = normalizePageSize(pageSize);
			}

			if (selectedTimezone && selectedTimezone.value) {
				this.selectedTimezone = selectedTimezone;
			}

			this.currentStartDate = getTodayStartWithTimezone(this.selectedTimezone);

			const cityIds = deserializeCitiesToIds(locationAvailabilityCity);

			if (cityIds.length > 0) {
				this.filters.locationAvailabilityCityIds = cityIds;
			}
		});
	};

	saveFiltersToStorage = () => {
		saveFilters({
			filters: this.filters,
			displayDays: this.displayDays,
			pageSize: this.pageSize,
			selectedTimezone: this.selectedTimezone,
		});
	};

	get processedFilters() {
		const {
			display: _display,
			pageSize: _pageSize,
			locationAvailabilityCity: _cities,
			...filter
		} = this.filters;
		const { fromTimestamp, toTimestamp } = this._calculateDateRange();

		filter.fromTimestamp = moment(fromTimestamp).utc();
		filter.toTimestamp = moment(toTimestamp).utc();

		return filter;
	}

	get isReadyShowResult() {
		return (
			!this.isTimelineError && !this.isTimelineLoading && this.timelineData.drivers?.length !== 0
		);
	}

	get isNothingFound() {
		return (
			!this.isTimelineError && !this.isTimelineLoading && this.timelineData.drivers?.length === 0
		);
	}

	get errorText() {
		if (!this.error) return '';
		return (
			this.error.graphQLErrors?.reduce(function formatError(acc, err, i) {
				return `${acc}\n Error ${i}: ${err.message}`;
			}, '') || CREW_TIMELINE_TEXT.errorText
		);
	}

	fetchTimeline = async () => {
		this._fetchRequestId += 1;
		const requestId = this._fetchRequestId;
		const isLatest = () => requestId === this._fetchRequestId;

		runInAction(() => {
			this.isTimelineLoading = true;
			this.isTimelineError = false;
		});

		if (this.useMockData) {
			runInAction(() => {
				const timezoneValue = this._resolveTimezone();
				const { fromTimestamp, toTimestamp } = this._calculateDateRange();
				const mockedResults = buildMockedTimelineResults(this.currentStartDate);

				this.timelineData = mapCrewTimelineTable(
					mockedResults,
					fromTimestamp,
					toTimestamp,
					timezoneValue
				);
				this.rawTimelineResults = mockedResults;
				this.pageCount = { size: mockedResults.length, page: 0 };
				this.activePage = 0;
				this.isTimelineLoading = false;
				this.error = null;
			});
			return;
		}

		try {
			const { data } = await client.query({
				query: FETCH_TIMELINE,
				variables: {
					pagination: { page: this.activePage, size: this.pageSize },
					filter: this.processedFilters,
				},
				fetchPolicy: 'no-cache',
				context: { cancelGroup: 'crewTimeline' },
			});

			if (!isLatest()) return;
			if (!data) return;

			const pageInfo = data?.crewMemberTimeline?.page || DEFAULT_PAGE_NUMBER;

			runInAction(() => {
				const results = data?.crewMemberTimeline?.results || [];

				this.pageCount = pageInfo;
				this.activePage = data?.crewMemberTimeline?.page?.page || DEFAULT_PAGE_NUMBER;

				const timezoneValue = this._resolveTimezone();

				if (results.length > 0) {
					const { fromTimestamp, toTimestamp } = this._calculateDateRange();

					this.rawTimelineResults = results;
					this.timelineData = mapCrewTimelineTable(
						results,
						fromTimestamp,
						toTimestamp,
						timezoneValue
					);
				} else {
					this.rawTimelineResults = [];
					this.timelineData = { drivers: [], dateRange: [], timezone: timezoneValue };
				}

				this.isTimelineLoading = false;
				this.error = null;
			});
		} catch (error) {
			if (!isLatest()) return;

			notificationStore.setNotification(standardError);

			runInAction(() => {
				this.isTimelineLoading = false;
				this.isTimelineError = true;
				this.error = error;
				this.timelineData = {
					drivers: [],
					dateRange: [],
					timezone: this._resolveTimezone(),
				};
				this.rawTimelineResults = [];
			});
		}
	};

	mergeExtraActivitySequence = async (sequence) => {
		const { results, didPatch } = replaceSequenceInTimelineResults(
			this.rawTimelineResults,
			sequence
		);

		if (!didPatch) {
			await this.fetchTimeline();
			return false;
		}

		const timezoneValue = this._resolveTimezone();
		const { fromTimestamp, toTimestamp } = this._calculateDateRange();

		runInAction(() => {
			this.rawTimelineResults = results;
			this.timelineData = mapCrewTimelineTable(results, fromTimestamp, toTimestamp, timezoneValue);
		});

		return true;
	};

	rejectExtraActivityAssignment = async (workAssignmentKey) => {
		runInAction(() => {
			this.isDeletingExtraActivity = true;
		});

		try {
			const sequence = await rejectExtraActivityAssignment(workAssignmentKey);

			if (!sequence) return false;

			return this.mergeExtraActivitySequence(sequence);
		} finally {
			runInAction(() => {
				this.isDeletingExtraActivity = false;
			});
		}
	};

	setFilters = (updatedFilters) => {
		const filtersChanged = JSON.stringify(this.filters) !== JSON.stringify(updatedFilters);

		if (!filtersChanged) return;

		const displayChanged =
			updatedFilters.display !== undefined && updatedFilters.display !== this.displayDays;
		const nextPageSize = normalizePageSize(updatedFilters.pageSize);
		const pageSizeChanged = updatedFilters.pageSize !== undefined && nextPageSize !== this.pageSize;

		runInAction(() => {
			this.filters = { ...updatedFilters };

			if (updatedFilters.fromTimestamp) {
				this.currentStartDate = getStartOfDayInTimezone(
					updatedFilters.fromTimestamp,
					this.selectedTimezone
				);
			}

			if (displayChanged) {
				this.displayDays = updatedFilters.display || DEFAULT_DISPLAY_DAYS;

				if (!this.currentStartDate) {
					this.currentStartDate = getTodayStartWithTimezone(this.selectedTimezone);
				}
			}

			if (pageSizeChanged) {
				this.pageSize = nextPageSize;
				this.activePage = DEFAULT_PAGE_NUMBER;
			}
		});

		this.saveFiltersToStorage();
		this.fetchTimeline();
	};

	setSelectedTimezone = (timezone) => {
		if (timezone === null) return;

		runInAction(() => {
			this.selectedTimezone = timezone;
			this.currentStartDate = getTodayStartWithTimezone(timezone);
			this.timelineData = {
				...this.timelineData,
				timezone: timezone.value || moment.tz.guess(),
			};
		});

		this.saveFiltersToStorage();
		this.fetchTimeline();
	};

	navigateToPreviousPeriod = () => {
		if (!this.canNavigatePrevious) return;

		runInAction(() => {
			this.currentStartDate = subtractDaysInTimezone(
				this.currentStartDate,
				1,
				this.selectedTimezone
			);
		});

		this._navigateAndFetch();
	};

	navigateToNextPeriod = () => {
		if (!this.canNavigateNext) return;

		runInAction(() => {
			this.currentStartDate = addDaysInTimezone(this.currentStartDate, 1, this.selectedTimezone);
		});

		this._navigateAndFetch();
	};

	navigateToToday = () => {
		runInAction(() => {
			this.currentStartDate = getTodayStartWithTimezone(this.selectedTimezone);
		});

		this._navigateAndFetch();
	};

	get canNavigatePrevious() {
		if (!this.currentStartDate) return false;

		const tz = this._resolveTimezone();
		const startDay = moment(this.currentStartDate).tz(tz).startOf('day');

		return startDay.isAfter(this._calendarMinDate);
	}

	get canNavigateNext() {
		if (!this.currentStartDate) return false;

		const tz = this._resolveTimezone();
		const days = Math.max(1, Number(this.displayDays) || 1);
		const startDay = moment(this.currentStartDate).tz(tz).startOf('day');
		const lastVisibleDay = startDay.clone().add(days - 1, 'days');

		return lastVisibleDay.isBefore(this._calendarMaxDate);
	}

	get _calendarMinDate() {
		const tz = this._resolveTimezone();

		return moment().tz(tz).startOf('day').subtract(CALENDAR_RANGE_MONTHS, 'months');
	}

	get _calendarMaxDate() {
		const tz = this._resolveTimezone();

		return moment().tz(tz).startOf('day').add(CALENDAR_RANGE_MONTHS, 'months');
	}

	setActiveItem = (item) => {
		runInAction(() => {
			this.activeItem = item;
		});
	};

	removeActiveItem = () => {
		runInAction(() => {
			this.activeItem = null;
		});
	};

	get activeShiftId() {
		return this.activeItem?.shiftId ?? null;
	}

	isActiveShift = (shiftId) => {
		return this.activeItem?.shiftId === shiftId;
	};

	get activeItemId() {
		return this.activeItem?.id ?? null;
	}

	setUseMockData = (value) => {
		runInAction(() => {
			this.useMockData = value;
		});
	};

	toQueryParams = () => {
		return serializeToQueryParams({
			filters: this.filters,
			displayDays: this.displayDays,
			pageSize: this.pageSize,
			selectedTimezone: this.selectedTimezone,
		});
	};

	applyQueryParams = (searchParams) => {
		const parsed = fromQueryParams(searchParams);
		if (!parsed.hasParams) return false;

		runInAction(() => {
			if (parsed.operatorType) this.filters.operatorType = parsed.operatorType;

			if (parsed.displayDays) this.displayDays = parsed.displayDays;

			if (parsed.pageSize) this.pageSize = normalizePageSize(parsed.pageSize);

			if (parsed.selectedTimezone) {
				this.selectedTimezone = parsed.selectedTimezone;
			}

			this.currentStartDate = getTodayStartWithTimezone(this.selectedTimezone);

			if (parsed.group) this.filters.groupsIds = [parsed.group];

			if (parsed.status) this.filters.status = parsed.status;

			if (parsed.cityIds) this.filters.locationAvailabilityCityIds = parsed.cityIds;
		});

		this.saveFiltersToStorage();
		return true;
	};

	dispose() {
		if (this._disposeFetch) {
			this._disposeFetch();
			this._disposeFetch = null;
		}
	}

	_resolveTimezone = () => {
		return this.selectedTimezone?.value || moment.tz.guess();
	};

	_calculateDateRange = () => {
		const tz = this._resolveTimezone();
		const fromTimestamp = getStartOfDayInTimezone(this.currentStartDate, this.selectedTimezone);
		const toTimestamp = moment(fromTimestamp)
			.tz(tz)
			.add(this.displayDays - 1, 'days')
			.endOf('day')
			.toISOString();

		return { fromTimestamp, toTimestamp };
	};

	_navigateAndFetch = () => {
		const { fromTimestamp, toTimestamp } = this._calculateDateRange();

		this.filters = { ...this.filters, fromTimestamp, toTimestamp };

		this.fetchTimeline();
	};

	_createFetchReaction = () => {
		return reaction(
			() => [toJS(this.filters), this.activePage, this.pageSize, this.useMockData],
			() => {
				this.fetchTimeline();
			},
			{
				fireImmediately: false,
				equals: comparer.structural,
				name: 'fetch-timeline-on-filters-or-page',
			}
		);
	};
}

export const crewTimelineStore = new CrewTimelineStore();
